<template>
    <div>
        <h5>{{ $t('account.update_password') }}</h5>

        <form @submit.prevent="submitForm">
            <ValidationObserver v-slot="{ invalid, dirty }" ref="form">
                <ValidationProvider :name="$t('account.password_update.password')" rules="required|min:8|password" v-slot="{ errors }" class="flex-grow-1" vid="password">
                    <div class="form-group row">
                        <label class="form-control-label col-4">{{ $t('account.password_update.password') }}*</label>
                        <div class="col-8">
                            <div class="input-group">
                                <input
                                    :disabled="isLoading"
                                    :type="passwordVisible ? 'text' : 'password'"
                                    class="form-control form-control-sm h-auto"
                                    v-model="formValues.password"
                                    :class="{ 'is-invalid': errors.length }"
                                />

                                <div class="input-group-append">
                                    <span class="input-group-text" @click="passwordVisible = !passwordVisible" style="cursor: pointer;">
                                        <icon :name="passwordVisible ? 'eye-slash' : 'eye'" style="fill: black !important;"></icon>
                                    </span>
                                </div>

                                <div v-if="errors.length" class="invalid-feedback">{{ errors[0] }}</div>
                            </div>
                        </div>
                    </div>
                </ValidationProvider>

                <ValidationProvider :name="$t('account.password_update.password_confirmation')" rules="required|confirmed:password|min:8|password" v-slot="{ errors }" class="flex-grow-1">
                    <div class="form-group row">
                        <label class="form-control-label col-4">{{ $t('account.password_update.password_confirmation') }}*</label>
                        <div class="col-8">
                            <div class="input-group">
                                <input
                                    :disabled="isLoading"
                                    :type="passwordConfirmationVisible ? 'text' : 'password'"
                                    class="form-control form-control-sm h-auto"
                                    v-model="formValues.password_confirmation"
                                    :class="{ 'is-invalid': errors.length }"
                                />

                                <div class="input-group-append">
                                    <span class="input-group-text" @click="passwordConfirmationVisible = !passwordConfirmationVisible" style="cursor: pointer;">
                                        <icon :name="passwordConfirmationVisible ? 'eye-slash' : 'eye'" style="fill: black !important;"></icon>
                                    </span>
                                </div>

                                <div v-if="errors.length" class="invalid-feedback">{{ errors[0] }}</div>
                            </div>
                        </div>
                    </div>
                </ValidationProvider>

                <button type="submit" class="btn btn-primary btn-sm" :disabled="invalid || !dirty">
                    {{ $t('account.password_update.submit') }}
                </button>
            </ValidationObserver>
        </form>

        <AccountConfirmModal
            v-if="showAccountConfirmModal"
            @confirmed="confirmed"
            @close="showAccountConfirmModal = false; reset()"
            :error-message="errorMessage"
            :confirm-text="$t('account.password_update.confirmation_description')"
        />
    </div>
</template>

<script setup>
    import { ref } from 'vue';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import AccountConfirmModal from '../components/AccountConfirmModal.vue';
    import { AccountService } from '../services/accountService';
    import { notifyError, notifySuccess } from '../utils/notify';
    import i18n from '../utils/locale';

    const emit = defineEmits(['loading']);

    const form = ref(null);

    const showAccountConfirmModal = ref(false);
    const passwordConfirmationVisible = ref(false);
    const passwordVisible = ref(false);

    const isLoading = ref(false);
    const errorMessage = ref(null);

    const formValues = ref({
        password: null,
        password_confirmation: null,
    });

    function reset() {
        formValues.value = {
            password: null,
            password_confirmation: null,
        };

        form.value.reset();
    }

    function submitForm() {
        showAccountConfirmModal.value = true;
    }

    async function confirmed(token) {
        emit('loading', true);
        isLoading.value = true;
        errorMessage.value = null;

        try {
            await AccountService.getInstance().updatePassword(formValues.value, token);
            showAccountConfirmModal.value = false;

            notifySuccess(i18n.t('account.wijziging_opgeslagen'));
        } catch (exception) {
            errorMessage.value = exception?.response?.data?.message || i18n.t('general.something_went_wrong');
            notifyError(exception);
        } finally {
            isLoading.value = false;
            emit('loading', false);
        }
    }
</script>
